/**
 * Navigation Store - GPS routing and directions state
 * Supports route priority: Fastest vs Safest Air
 */

import { create } from 'zustand';
import type { Coordinates } from '@/types';

export type RoutePriority = 'fastest' | 'safest';

export interface SearchResult {
  id: string;
  name: string;
  address: string;
  coordinates: Coordinates;
  distance?: number; // meters from current location
  category?: string;
}

export interface RouteStep {
  instruction: string;
  distance: number; // meters
  duration: number; // seconds
  maneuver: 'straight' | 'turn-left' | 'turn-right' | 'u-turn' | 'arrive' | 'depart';
  coordinates: Coordinates;
  roadType?: 'highway' | 'main' | 'residential' | 'path' | 'park';
  segmentNI?: number; // Neural Index for this segment
}

export interface RouteOption {
  id: string;
  priority: RoutePriority;
  origin: Coordinates;
  destination: Coordinates;
  destinationName: string;
  totalDistance: number; // meters
  totalDuration: number; // seconds
  steps: RouteStep[];
  cognitiveScore: number; // 0-100
  arrivalNeuralIndex: number; // predicted NI at arrival
  polyline: Coordinates[];
  averageNI: number; // average Neural Index along route
  exposureScore: number; // lower = less pollution exposure
}

export interface ActiveRoute extends RouteOption {}

interface NavigationState {
  // Search
  searchQuery: string;
  searchResults: SearchResult[];
  isSearching: boolean;
  selectedDestination: SearchResult | null;

  // Route Priority Selection
  prioritySelected: boolean; // Has user selected their priority preference?

  // Route Options
  routePriority: RoutePriority;
  routeOptions: RouteOption[];
  selectedRouteId: string | null;

  // Navigation
  isNavigating: boolean;
  activeRoute: ActiveRoute | null;
  currentStepIndex: number;
  distanceToNextStep: number;

  // Timing
  estimatedArrival: Date | null;
  elapsedTime: number; // seconds

  // Demo mode
  isDemoMode: boolean;

  // Actions
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: SearchResult[]) => void;
  setIsSearching: (searching: boolean) => void;
  selectDestination: (destination: SearchResult | null) => void;
  setRoutePriority: (priority: RoutePriority) => void;
  setPrioritySelected: (selected: boolean) => void;
  setRouteOptions: (options: RouteOption[]) => void;
  selectRoute: (routeId: string) => void;
  startNavigation: (route: ActiveRoute) => void;
  stopNavigation: () => void;
  updateProgress: (stepIndex: number, distanceToNext: number) => void;
  advanceStep: () => void;
  setDemoMode: (demo: boolean) => void;
  clearSearch: () => void;
}

export const useNavigationStore = create<NavigationState>()((set, get) => ({
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  selectedDestination: null,
  prioritySelected: false, // User must choose priority first
  routePriority: 'safest', // Default to safest air
  routeOptions: [],
  selectedRouteId: null,
  isNavigating: false,
  activeRoute: null,
  currentStepIndex: 0,
  distanceToNextStep: 0,
  estimatedArrival: null,
  elapsedTime: 0,
  isDemoMode: false, // Will be set based on Mapbox token availability

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  setSearchResults: (searchResults) => set({ searchResults, isSearching: false }),

  setIsSearching: (isSearching) => set({ isSearching }),

  selectDestination: (selectedDestination) => set({ selectedDestination }),

  setRoutePriority: (routePriority) => {
    set({ routePriority, prioritySelected: true });
    // Auto-select the route matching new priority
    const { routeOptions } = get();
    const matchingRoute = routeOptions.find(r => r.priority === routePriority);
    if (matchingRoute) {
      set({ selectedRouteId: matchingRoute.id });
    }
  },

  setPrioritySelected: (prioritySelected) => set({ prioritySelected }),

  setRouteOptions: (routeOptions) => {
    const { routePriority } = get();
    const matchingRoute = routeOptions.find(r => r.priority === routePriority);
    set({
      routeOptions,
      selectedRouteId: matchingRoute?.id || routeOptions[0]?.id || null,
    });
  },

  selectRoute: (selectedRouteId) => set({ selectedRouteId }),

  startNavigation: (activeRoute) => {
    const eta = new Date(Date.now() + activeRoute.totalDuration * 1000);
    set({
      isNavigating: true,
      activeRoute,
      currentStepIndex: 0,
      distanceToNextStep: activeRoute.steps[0]?.distance || 0,
      estimatedArrival: eta,
      elapsedTime: 0,
    });
  },

  stopNavigation: () => set({
    isNavigating: false,
    activeRoute: null,
    currentStepIndex: 0,
    distanceToNextStep: 0,
    estimatedArrival: null,
    elapsedTime: 0,
    selectedDestination: null,
    prioritySelected: false,
    routeOptions: [],
    selectedRouteId: null,
  }),

  updateProgress: (currentStepIndex, distanceToNextStep) => set({
    currentStepIndex,
    distanceToNextStep,
  }),

  advanceStep: () => set((state) => {
    if (!state.activeRoute) return state;
    const nextIndex = state.currentStepIndex + 1;
    if (nextIndex >= state.activeRoute.steps.length) return state;
    return {
      currentStepIndex: nextIndex,
      distanceToNextStep: state.activeRoute.steps[nextIndex]?.distance || 0,
    };
  }),

  setDemoMode: (isDemoMode) => set({ isDemoMode }),

  clearSearch: () => set({
    searchQuery: '',
    searchResults: [],
    selectedDestination: null,
    prioritySelected: false,
    routeOptions: [],
    selectedRouteId: null,
  }),
}));

// Demo data - sample search results
export const DEMO_LOCATIONS: SearchResult[] = [
  {
    id: '1',
    name: 'Golden Gate Park',
    address: '501 Stanyan St, San Francisco, CA',
    coordinates: { lat: 37.7694, lng: -122.4862 },
    distance: 2400,
    category: 'Park',
  },
  {
    id: '2',
    name: 'Dolores Park',
    address: 'Dolores St & 19th St, San Francisco, CA',
    coordinates: { lat: 37.7596, lng: -122.4269 },
    distance: 1800,
    category: 'Park',
  },
  {
    id: '3',
    name: 'Ferry Building',
    address: '1 Ferry Building, San Francisco, CA',
    coordinates: { lat: 37.7955, lng: -122.3937 },
    distance: 3200,
    category: 'Landmark',
  },
  {
    id: '4',
    name: 'Crissy Field',
    address: '603 Mason St, San Francisco, CA',
    coordinates: { lat: 37.8039, lng: -122.4638 },
    distance: 4100,
    category: 'Park',
  },
  {
    id: '5',
    name: 'Twin Peaks',
    address: '501 Twin Peaks Blvd, San Francisco, CA',
    coordinates: { lat: 37.7544, lng: -122.4477 },
    distance: 2900,
    category: 'Viewpoint',
  },
];

/**
 * Calculate realistic walking time
 * Factors: distance, road type, terrain
 * Average walking speed: 4.5-5.5 km/h on flat ground
 */
function calculateWalkingTime(distance: number, roadType: string): number {
  // Base speed in m/s (5 km/h = 1.39 m/s)
  let speedMs = 1.39;

  // Adjust for road type
  switch (roadType) {
    case 'path':
    case 'park':
      speedMs = 1.3; // Slightly slower on paths
      break;
    case 'residential':
      speedMs = 1.4; // Normal walking
      break;
    case 'main':
      speedMs = 1.35; // More crossings, traffic lights
      break;
    case 'highway':
      speedMs = 1.2; // Avoid, slower due to detours
      break;
    default:
      speedMs = 1.39;
  }

  // Add 10% buffer for real-world conditions (stops, turns, etc.)
  return Math.round((distance / speedMs) * 1.1);
}

/**
 * Calculate Haversine distance between two coordinates
 */
function haversineDistance(p1: Coordinates, p2: Coordinates): number {
  const R = 6371000; // Earth radius in meters
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Generate both fastest and safest routes
 */
export function generateRouteOptions(
  origin: Coordinates,
  destination: SearchResult,
  baseNI: number = 75
): RouteOption[] {
  const actualDistance = haversineDistance(origin, destination.coordinates);

  // Generate FASTEST route (direct, uses main roads)
  const fastestRoute = generateRoute(origin, destination, 'fastest', baseNI, actualDistance);

  // Generate SAFEST route (longer, avoids pollution hotspots)
  const safestRoute = generateRoute(origin, destination, 'safest', baseNI, actualDistance);

  return [fastestRoute, safestRoute];
}

/**
 * Generate a single route with priority
 */
function generateRoute(
  origin: Coordinates,
  destination: SearchResult,
  priority: RoutePriority,
  baseNI: number,
  actualDistance: number
): RouteOption {
  const isSafest = priority === 'safest';

  // Safest route is ~15-25% longer but avoids main roads
  const distanceMultiplier = isSafest ? 1.15 + Math.random() * 0.1 : 1;
  const routeDistance = Math.round(actualDistance * distanceMultiplier);

  // Road types differ by priority
  const roadTypes = isSafest
    ? ['residential', 'path', 'park', 'residential', 'path']
    : ['main', 'main', 'residential', 'main', 'residential'];

  // Neural Index differs by route
  const routeNIBonus = isSafest ? 8 + Math.random() * 5 : 0;
  const routeNIPenalty = isSafest ? 0 : 5 + Math.random() * 8;

  // Generate waypoints between origin and destination
  const numSteps = 4 + Math.floor(Math.random() * 2);
  const steps: RouteStep[] = [];

  let currentPos = { ...origin };
  let remainingDistance = routeDistance;

  // Departure step
  const departRoadType = roadTypes[0];
  const departDistance = Math.round(remainingDistance * 0.2);
  const departNI = Math.round(baseNI + (isSafest ? 5 : -3));

  steps.push({
    instruction: isSafest
      ? 'Head through the quiet residential area'
      : 'Head north on the main road',
    distance: departDistance,
    duration: calculateWalkingTime(departDistance, departRoadType),
    maneuver: 'depart',
    coordinates: currentPos,
    roadType: departRoadType as RouteStep['roadType'],
    segmentNI: departNI,
  });

  remainingDistance -= departDistance;

  // Update position
  const latStep = (destination.coordinates.lat - origin.lat) / numSteps;
  const lngStep = (destination.coordinates.lng - origin.lng) / numSteps;

  // Middle steps
  const streetNames = isSafest
    ? ['Park Lane', 'Garden Path', 'Green Street', 'Riverside Walk']
    : ['Main Street', 'Commerce Ave', 'Highway Bypass', 'Central Road'];

  const maneuvers: RouteStep['maneuver'][] = ['turn-left', 'straight', 'turn-right', 'straight'];

  for (let i = 1; i < numSteps - 1; i++) {
    currentPos = {
      lat: origin.lat + latStep * i + (Math.random() - 0.5) * 0.002,
      lng: origin.lng + lngStep * i + (Math.random() - 0.5) * 0.002,
    };

    const roadType = roadTypes[i % roadTypes.length];
    const stepDistance = Math.round(remainingDistance / (numSteps - i));
    const stepNI = Math.round(baseNI + routeNIBonus - routeNIPenalty + (Math.random() - 0.5) * 5);

    steps.push({
      instruction: `${maneuvers[i % maneuvers.length] === 'straight' ? 'Continue on' : `Turn ${maneuvers[i % maneuvers.length].replace('turn-', '')} onto`} ${streetNames[i % streetNames.length]}`,
      distance: stepDistance,
      duration: calculateWalkingTime(stepDistance, roadType),
      maneuver: maneuvers[i % maneuvers.length],
      coordinates: currentPos,
      roadType: roadType as RouteStep['roadType'],
      segmentNI: stepNI,
    });

    remainingDistance -= stepDistance;
  }

  // Arrival step
  steps.push({
    instruction: `Arrive at ${destination.name}`,
    distance: 0,
    duration: 0,
    maneuver: 'arrive',
    coordinates: destination.coordinates,
    roadType: 'residential',
    segmentNI: Math.round(baseNI + routeNIBonus),
  });

  // Calculate totals
  const totalDistance = steps.reduce((sum, s) => sum + s.distance, 0);
  const totalDuration = steps.reduce((sum, s) => sum + s.duration, 0);
  const averageNI = Math.round(
    steps.reduce((sum, s) => sum + (s.segmentNI || baseNI), 0) / steps.length
  );

  // Exposure score: lower is better (less pollution exposure)
  // Based on time spent * pollution level
  const exposureScore = Math.round(
    steps.reduce((sum, s) => {
      const niDeficit = 100 - (s.segmentNI || baseNI);
      return sum + (s.duration / 60) * niDeficit;
    }, 0)
  );

  // Arrival NI prediction
  const arrivalNeuralIndex = Math.round(Math.min(100, Math.max(40, averageNI + (isSafest ? 3 : -2))));

  const polyline: Coordinates[] = steps.map(s => s.coordinates);

  return {
    id: `route_${priority}_${Date.now()}`,
    priority,
    origin,
    destination: destination.coordinates,
    destinationName: destination.name,
    totalDistance,
    totalDuration,
    steps,
    cognitiveScore: averageNI,
    arrivalNeuralIndex,
    polyline,
    averageNI,
    exposureScore,
  };
}

// Legacy function for backwards compatibility
export function generateDemoRoute(
  origin: Coordinates,
  destination: SearchResult
): ActiveRoute {
  const options = generateRouteOptions(origin, destination);
  return options.find(r => r.priority === 'safest') || options[0];
}

// Selectors
export const selectIsNavigating = (state: NavigationState) => state.isNavigating;
export const selectActiveRoute = (state: NavigationState) => state.activeRoute;
export const selectSearchResults = (state: NavigationState) => state.searchResults;
export const selectRoutePriority = (state: NavigationState) => state.routePriority;
export const selectRouteOptions = (state: NavigationState) => state.routeOptions;
